import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Sparkle, Crown, ArrowRight, ArrowLeft, Globe, YoutubeLogo, LockSimple, Users } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import WaysToUseModal from "@/components/WaysToUseModal";
import { PUBLIC_IDEAS, YOUTUBE_IDEAS, PERSONAL_IDEAS, ORGANIZER_IDEAS } from "@/lib/waysToUse";

const KIND_WAYS = {
  public:    { ideas: PUBLIC_IDEAS,    title: "Unique ways to use a Public silo" },
  twin:      { ideas: YOUTUBE_IDEAS,   title: "Unique ways to use a YouTube Twin silo" },
  personal:  { ideas: PERSONAL_IDEAS,  title: "Unique ways to use a Personal silo" },
  organizer: { ideas: ORGANIZER_IDEAS, title: "Unique ways to use an Organizer silo" },
};

const TEMPLATES = [
  { id: "academic", label: "Academic", desc: "Editorial · serif · light cream" },
  { id: "tactical", label: "Tactical", desc: "Dark slate · monospace · neon mint" },
  { id: "modern", label: "Modern", desc: "Clean sans · peach accent" },
];

// Jul 2026 (Randy) — Step 0: pick what KIND of silo you're creating.
// "public"  → classic flow, "twin" → public silo + toolkit buffet popped with
// Workbench/YouTube/Macros pre-selected, "personal" → hidden-forever private
// silo + buffet popped with Staging/AI Calendar pre-selected.
const KINDS = [
  {
    id: "public", label: "Public Silo", icon: Globe,
    desc: "A Q&A silo for the world. Discoverable, shareable, monetizable — the classic My Silo.",
  },
  {
    id: "twin", label: "YouTube Twin Silo", icon: YoutubeLogo,
    desc: "A public silo built from a whole YouTube channel. Opens the toolkit checkout with the Workbench, YouTube Twin and Macros pre-selected.",
  },
  {
    id: "personal", label: "Personal Silo", icon: LockSimple,
    desc: "Private forever — never public, never discoverable. Your life organizer: capture pad, question bank, scheduled pings and the AI Calendar.",
  },
  {
    id: "organizer", label: "Organizer Silo", icon: Users,
    desc: "Private forever — family or team HQ. Household setup, calendar, chore streaks, reward store, family leaderboard and weekly digest. Kid tiles, PIN sign-in, email invitees.",
  },
];

export default function CreateSiloDialog({ open, onClose, onCreated }) {
  const [kind, setKind] = useState(null); // null = Step-0 picker
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [template, setTemplate] = useState("academic");
  const [leadIn, setLeadIn] = useState("");
  const [loading, setLoading] = useState(false);
  const [waysKind, setWaysKind] = useState(null); // opens WaysToUseModal for this kind
  // When the server returns the silo-limit 402 (3 silos on Creator tier),
  // we surface a friendly upgrade modal instead of a raw toast. Stored as
  // the error detail so the modal can show the exact server message.
  const [siloLimitMsg, setSiloLimitMsg] = useState("");
  const nav = useNavigate();

  // Every fresh open restarts at the Step-0 picker.
  useEffect(() => { if (open) setKind(null); }, [open]);

  if (!open && !siloLimitMsg) return null;

  const isPersonal = kind === "personal";
  const isOrganizer = kind === "organizer";
  const isPrivate = isPersonal || isOrganizer;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/silos", {
        name,
        tagline: isPrivate ? "" : tagline,
        template,
        silo_kind: isOrganizer ? "organizer" : (isPersonal ? "personal" : "public"),
        // Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md item G) — the picker
        // already asked "is this a YouTube twin?" on step 0 and then threw the
        // answer away, so the server had to infer it later from the content.
        // Randy: "when a new youtube digital twin silo is created... relevant
        // links should be on by default." Sending the answer is what lets the
        // server honour that at creation time.
        is_youtube_twin: kind === "twin",
      });
      toast.success(
        isOrganizer ? `Organizer silo "${r.data.name}" created — private forever.`
        : isPersonal ? `Personal silo "${r.data.name}" created — private forever.`
        : `Silo "${r.data.name}" created.`
      );
      onCreated?.(r.data, kind);
      onClose();
    } catch (err) {
      // May 11, 2026 — surface the silo cap as a real upgrade modal,
      // not a toast. 402 = payment required, used by the cap gate.
      if (err.response?.status === 402 && err.response?.data?.detail?.includes("Creator tier is capped")) {
        setSiloLimitMsg(err.response.data.detail);
      } else {
        toast.error(err.response?.data?.detail || "Could not create silo");
      }
    } finally { setLoading(false); }
  };

  if (siloLimitMsg) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="silo-limit-modal">
        <div className="absolute inset-0 bg-[#1C1B1A]/60 backdrop-blur-sm" onClick={() => { setSiloLimitMsg(""); onClose?.(); }} />
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="modal-safe relative z-10 w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#A89360] to-[#8C7846] mx-auto flex items-center justify-center mb-3 text-white">
              <Crown size={26} weight="duotone" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A89360] mb-1.5">Silo limit reached</div>
            <h2 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight mb-2">
              Want to build more silos?
            </h2>
            <p className="text-sm text-[#6B6A68] leading-relaxed mb-5">
              You're at the Creator tier's 3-silo limit. Upgrade to <strong>Creator Pro</strong> to expand your portfolio to <strong>more silos</strong> — and unlock the full building toolkit.
            </p>
            <div className="bg-[#FDF1ED]/50 border border-[#C16E5A]/30 rounded-md p-3 mb-5 text-left">
              <div className="text-xs text-[#1C1B1A] space-y-1.5">
                <div className="flex items-start gap-2">
                  <span className="text-[#4A5D4E]">✓</span>
                  <span><strong>More silos.</strong> More space to build distinct AI twins.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#4A5D4E]">✓</span>
                  <span><strong>Boost & Macros</strong> — deeper AI answers and automated YouTube ingestion.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#4A5D4E]">✓</span>
                  <span><strong>Collaboration & AI Assistant</strong> — invite teammates and get a build-side co-pilot.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#4A5D4E]">✓</span>
                  <span><strong>Silo Wars</strong> — challenge other silos in your niche, prove accuracy, rank up.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#4A5D4E]">✓</span>
                  <span><strong>YouTube Digital Twin Kit</strong> — turn a whole channel into a talking twin.</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setSiloLimitMsg(""); onClose?.(); }}
                data-testid="silo-limit-cancel"
                className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] px-4 py-2"
              >
                Maybe later
              </button>
              <button
                onClick={() => { setSiloLimitMsg(""); onClose?.(); nav("/dashboard/account-settings"); }}
                data-testid="silo-limit-upgrade"
                className="text-sm bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-2.5 rounded-md flex items-center gap-1.5"
              >
                Upgrade to Creator Pro <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------- Step 0: what kind of silo? ----------
  if (!kind) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-silo-kind-step">
        <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="modal-safe relative z-10 w-full max-w-lg bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b hairline">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center"><Sparkle size={18} weight="duotone" /></div>
              <div>
                <div className="font-serif-display text-lg leading-none">What are you creating?</div>
                <div className="text-xs text-[#6B6A68] uppercase tracking-wide mt-1">Step 1 of 2</div>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
          </div>
          <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
            {KINDS.map((k) => (
              <div key={k.id} role="button" tabIndex={0}
                onClick={() => setKind(k.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setKind(k.id); } }}
                data-testid={`silo-kind-${k.id}`}
                className="w-full text-left p-4 rounded-lg border hairline hover:border-[#4A5D4E] hover:bg-[#F0F1EE]/50 transition-colors flex items-start gap-3 group cursor-pointer">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${k.id === "personal" || k.id === "organizer" ? "bg-[#F0F1EE] text-[#4A5D4E]" : "bg-[#1C1B1A] text-[#FDFCF8]"}`}>
                  <k.icon size={20} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2">
                    {k.label}
                    {(k.id === "personal" || k.id === "organizer") && (
                      <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#4A5D4E] font-medium">Hidden forever</span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B6A68] mt-1 leading-relaxed">{k.desc}</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setWaysKind(k.id); }}
                    data-testid={`silo-kind-ways-link-${k.id}`}
                    className="mt-2 text-[11.5px] text-[#4A5D4E] hover:text-[#1C1B1A] hover:underline inline-flex items-center gap-1 font-medium"
                  >
                    Unique ways to use {k.id === "public" ? "a Public" : k.id === "twin" ? "a YouTube Twin" : k.id === "personal" ? "a Personal" : "an Organizer"} silo <ArrowRight size={11} />
                  </button>
                </div>
                <ArrowRight size={16} className="ml-auto mt-2 shrink-0 text-[#6B6A68] group-hover:text-[#4A5D4E]" />
              </div>
            ))}
          </div>
        </motion.div>
        {waysKind && (
          <WaysToUseModal
            open={true}
            onClose={() => setWaysKind(null)}
            kind={waysKind}
            title={KIND_WAYS[waysKind]?.title}
            ideas={KIND_WAYS[waysKind]?.ideas || []}
          />
        )}
      </div>
    );
  }

  // ---------- Step 1: the details form ----------
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-silo-dialog">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
        className="modal-safe relative z-10 w-full max-w-lg bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setKind(null)} data-testid="silo-kind-back"
              className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]" title="Back to silo type">
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="font-serif-display text-lg leading-none">
                {isOrganizer ? "Create your organizer silo" : isPersonal ? "Create your personal silo" : kind === "twin" ? "Create your YouTube twin silo" : "Create a new silo"}
              </div>
              <div className="text-xs text-[#6B6A68] uppercase tracking-wide mt-1">Step 2 of 2</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {isPrivate && (
            <div className="bg-[#F0F1EE] border border-[#4A5D4E]/20 rounded-md p-3 flex items-start gap-2 text-xs text-[#4A5D4E]" data-testid={isOrganizer ? "organizer-silo-note" : "personal-silo-note"}>
              <LockSimple size={14} weight="fill" className="shrink-0 mt-0.5" />
              <span>This silo is <strong>private forever</strong> — hidden from search, discovery and the public web. It can never be made public. Only you (and collaborators you invite) can open it.</span>
            </div>
          )}
          <div>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Silo name</label>
            <input data-testid="silo-name-input" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]"
              placeholder={isOrganizer ? "e.g., The Smith Family HQ" : isPersonal ? "e.g., My Life OS" : "e.g., The Smith Sales Method"} />
          </div>
          {!isPrivate && (
            <div>
              <label className="block text-sm text-[#6B6A68] mb-1.5">Tagline (optional)</label>
              <input data-testid="silo-tagline-input" value={tagline} onChange={(e) => setTagline(e.target.value)}
                className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]"
                placeholder="e.g., A 12-week framework" />
            </div>
          )}
          <div>
            <label className="block text-sm text-[#6B6A68] mb-2">UI template</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => setTemplate(t.id)} data-testid={`template-${t.id}`}
                  className={`text-left p-3 rounded-md border transition-colors ${template === t.id ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`}>
                  <div className="text-sm font-medium text-[#1C1B1A]">{t.label}</div>
                  <div className="text-[11px] text-[#6B6A68] mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
          {/* Oct 6 2026 — the "Custom attribution lead-in" field was removed
              with the feature itself (see resolve_attribution_prefix). Voice
              Kit and Tone Architect own how a silo sounds. */}
        </div>

        <div className="px-6 py-4 bg-[#FDFCF8] border-t hairline flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm">Cancel</button>
          <button type="submit" disabled={loading || !name.trim()} data-testid="silo-create-submit"
            className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
            {loading ? "Creating…" : isOrganizer ? "Create organizer silo" : isPersonal ? "Create personal silo" : "Create silo"}
          </button>
        </div>
      </motion.form>
    </div>
  );
}
